import { languageSupport } from '@unpispas/upp-base';
import { I18nValue } from './model/objects/i18nvalue';

type _TrArgs = Array<string | number | null | undefined>;

/** Resolves a catalog translation row for display in the active UI language. */
class I18nView {
    constructor(
        private readonly _value: I18nValue,
        private readonly _lang: languageSupport,
    ) {}

    /** Supported language codes from the associated {@link languageService}. */
    GetLanguages(): string[] {
        return this._lang._lang.GetLanguages();
    }

    /** Whether the row has no usable text in any supported language. */
    get empty(): boolean {
        for (const _lang of this.GetLanguages()) {
            const _text = this._value[_lang] ?? null;
            if (_text !== null && _text !== undefined && String(_text).trim() !== '') {
                return false;
            }
        }
        return true;
    }

    /** Display label for the active UI language. */
    tr(): string | null {
        return this._value[(this._lang.language || 'ES').toLowerCase() as 'es'] ?? null;
    }
}

/**
 * Language support for UI components that show both app strings and catalog text.
 *
 * Use {@link i18nSupport.tr} for `@…` keys and for `I18nValue` rows from the catalog.
 * Use {@link i18nSupport.empty} to validate that a catalog row has text before save.
 *
 */
export class i18nSupport extends languageSupport {
    private readonly _i18nViews = new WeakMap<I18nValue, I18nView>();

    /** Cached {@link I18nView} for a catalog row (one view per row instance). */
    private _I18nView(_value: I18nValue): I18nView {
        let _view = this._i18nViews.get(_value);
        if (!_view) {
            _view = new I18nView(_value, this);
            this._i18nViews.set(_value, _view);
        }
        return _view;
    }

    /** Translates an app string key or a catalog row. */
    override tr(id: string, args?: _TrArgs): string;
    override tr(row: I18nValue | null): string | null;
    override tr(_value: string | I18nValue | null, args: _TrArgs = []): string | null {
        if (typeof _value === 'string') {
            return super.tr(_value, args);
        }

        if (!_value) {
            return null;
        }
        return this._I18nView(_value).tr();
    }

    /** Whether a catalog row has no text in any supported language. */
    empty(_value: I18nValue | null): boolean {
        if (!_value) {
            return true;
        }

        return this._I18nView(_value).empty;
    }
}
